import React, { useState } from 'react'
import { Box, InputBase, Typography, IconButton } from '@mui/material'
import OpenEyeIcon from '@/assets/OpenEye icon.svg'
import EyeIcon from '@/assets/eye icon.svg'
import { useAppTheme } from '@/theme/ThemeContext'

interface BorderColors {
  default: string
  hover: string
  focus: string
}

interface LoginFieldProps {
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  hasError?: boolean
  borderColors?: BorderColors
  backgroundColor?: string
  accentColor?: string
  accentWidth?: number
  isDarkMode?: boolean
}

const DEFAULT_COLORS: BorderColors = {
  default: '#E0E3E7',
  hover: '#B0BEC5',
  focus: '#90A4AE',
}

const LoginInput: React.FC<LoginFieldProps> = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  hasError = false,
  borderColors = DEFAULT_COLORS,
  backgroundColor,
  accentColor = '#4CAF50',
  accentWidth = 20,
  isDarkMode,
}) => {
  const { mode } = useAppTheme()
  const isDark = isDarkMode !== undefined ? isDarkMode : mode === 'dark'

  const [isFocused, setIsFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const isPasswordField = type === 'password'
  const inputType = isPasswordField ? (showPassword ? 'text' : 'password') : type

  const float = isFocused || value.length > 0

  const resolvedBorderColors = isDark && borderColors === DEFAULT_COLORS
    ? { default: '#2D3F58', hover: '#3E5879', focus: '#7CAD44' }
    : borderColors

  const borderColor = hasError
    ? '#E53935'
    : isFocused
    ? resolvedBorderColors.focus
    : resolvedBorderColors.default

  const resolvedBgColor = backgroundColor ?? (isDark ? '#0f1f35' : '#FFFFFF')

  // Theme-aware colors
  const textColor = isDark ? '#FFFFFF' : '#0B4F6C'
  const labelColor = hasError
    ? '#E53935'
    : float
    ? (isDark ? '#94a3b8' : '#0B4F6C')
    : (isDark ? '#94a3b8' : '#0000009C')
  const placeholderColor = isDark ? '#64748b' : '#90A4AE'
  const iconColor = isDark ? '#64748b' : '#9E9E9E'

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        border: `1px solid ${borderColor}`,
        bgcolor: resolvedBgColor,
        height: '56px',
        boxShadow: isFocused
          ? isDark 
            ? '0px 6px 20px rgba(0, 0, 0, 0.3)' 
            : '0px 6px 20px rgba(13, 75, 120, 0.12)'
          : isDark
            ? '0px 4px 12px rgba(0, 0, 0, 0.2)'
            : '0px 4px 12px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      <InputBase
        type={inputType}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        inputProps={{
          'aria-label': label,
          dir: 'rtl',
        }}
        sx={{
          width: '100%',
          height: '100%',
          fontSize: '1.1rem',
          fontWeight: 600,
          color: textColor,
          bgcolor: 'transparent',
          '& input': {
            height: '100%',
            padding: isPasswordField ? '0 50px 0 100px' : '0 24px 0 100px',
            textAlign: 'right',
            color: `${textColor} !important`,
            WebkitTextFillColor: `${textColor} !important`,
            bgcolor: 'transparent',
          },
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0 1000px ${resolvedBgColor} inset !important`,
            WebkitTextFillColor: `${textColor} !important`,
            caretColor: textColor,
            transition: 'background-color 5000s ease-in-out 0s',
            borderRadius: 'inherit',
          },
          '& input::placeholder': {
            color: placeholderColor,
            opacity: 1,
            textAlign: 'right',
          },
        }}
      />

      {/* Password visibility toggle */}
      {isPasswordField && (
        <IconButton
          onClick={() => setShowPassword(!showPassword)}
          sx={{
            position: 'absolute',
            left: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 3,
            p: 0.5,
            color: iconColor,
            '&:hover': {
              color: isDarkMode ? '#94a3b8' : '#757575',
            },
          }}
        >
          <img
            src={showPassword ? OpenEyeIcon : EyeIcon}
            alt="toggle password visibility"
            style={{
              width: 20,
              height: 20,
              filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
            }}
          />
        </IconButton>
      )}

      <Typography
        variant="body2"
        sx={{
          position: 'absolute',
          right: 32,
          top: float ? 6 : '50%',
          transform: float ? 'translateY(0)' : 'translateY(-50%)',
          fontSize: float ? '0.7rem' : '0.95rem',
          color: labelColor,
          pointerEvents: 'none',
          zIndex: 3,
          transition: 'all 0.2s ease',
        }}
      >
        {label}
      </Typography>

      {accentColor && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: accentWidth,
            height: '100%',
            backgroundColor: accentColor,
            opacity: isFocused ? 1 : 0,
            transition: 'opacity 0.3s ease',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  )
}

export default LoginInput
